import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { MagnifyingGlass, X, ArrowRight } from "@phosphor-icons/react";

const kindLabels = { conversation: "Conversation", plan: "Plan", process: "Task", interview: "Interview", opportunity: "Opportunity", wisdom: "Lesson Learned", win: "Win" };
const kindClasses = { conversation: "k-conversation", plan: "k-plan", process: "k-process", interview: "k-interview", opportunity: "k-opportunity", wisdom: "k-wisdom", win: "k-win" };

// Fields on a sub-entry that are searchable client-side (mirrors the backend $or list).
const SEARCH_FIELDS = ["subject", "notes", "process_type", "leader", "position", "applicant_name"];

function escapeRegExp(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function Highlight({ text, term }) {
  if (!term || !text) return <>{text}</>;
  const splitRe = new RegExp(`(${escapeRegExp(term)})`, "ig");
  const lower = term.toLowerCase();
  const parts = String(text).split(splitRe);
  return (
    <>
      {parts.map((p, i) =>
        p && p.toLowerCase() === lower ? (
          <mark key={`${i}-${p}`} className="bg-yellow-200/70 text-slate-900 rounded px-0.5">{p}</mark>
        ) : (
          <span key={`${i}-${p}`}>{p}</span>
        )
      )}
    </>
  );
}

function subEntryText(s) {
  const bits = SEARCH_FIELDS.map((f) => s[f]).filter(Boolean);
  const arrays = [...(s.participants || []), ...(s.team || [])].filter(Boolean);
  return [...bits, ...arrays].join(" \n ").toLowerCase();
}

function snippet(s) {
  const line = s.notes || s.subject || s.process_type || s.position || "";
  return line.length > 220 ? line.slice(0, 217) + "…" : line;
}

export default function Search() {
  const [params, setParams] = useSearchParams();
  const initialQ = params.get("q") || "";
  const [term, setTerm] = useState(initialQ);
  const [committedTerm, setCommittedTerm] = useState(initialQ);
  const [selectedKinds, setSelectedKinds] = useState(new Set());
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const debounceRef = useRef();
  const inputRef = useRef();

  // Debounce the search term → committed term (used for the API call + client filter).
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setCommittedTerm(term.trim());
      const next = new URLSearchParams(params);
      if (term.trim()) next.set("q", term.trim()); else next.delete("q");
      setParams(next, { replace: true });
    }, 350);
    return () => clearTimeout(debounceRef.current);
    // params/setParams intentionally omitted — we only sync on term change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term]);

  const runSearch = useCallback(async (q) => {
    if (!q) { setLogs([]); return; }
    setLoading(true); setError(null);
    try {
      const { data } = await api.get(`/logs?q=${encodeURIComponent(q)}`);
      setLogs(data);
    } catch {
      setError("Couldn't run that search — try again in a moment");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { runSearch(committedTerm); }, [committedTerm, runSearch]);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const toggleKind = (k) => {
    setSelectedKinds((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  // Build a flat list of {log, sub} matches so the results can be shown as rows.
  const matches = useMemo(() => {
    if (!committedTerm) return [];
    const needle = committedTerm.toLowerCase();
    const kindFilter = selectedKinds.size ? selectedKinds : null;
    const rows = [];
    for (const l of logs) {
      // Also surface a "general notes" hit as its own row so it's not lost.
      const notesHit = (l.general_notes || "").toLowerCase().includes(needle);
      if (notesHit && (!kindFilter || kindFilter.has("__notes__"))) {
        rows.push({ log: l, sub: null, kind: "__notes__" });
      }
      for (const s of l.subentries || []) {
        if (kindFilter && !kindFilter.has(s.kind)) continue;
        if (subEntryText(s).includes(needle)) {
          rows.push({ log: l, sub: s, kind: s.kind });
        }
      }
    }
    return rows;
  }, [logs, committedTerm, selectedKinds]);

  const totalHits = matches.length;

  return (
    <div data-testid="search-page" className="space-y-6">
      <header>
        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Search</div>
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Find anything in your journal</h1>
        <p className="text-sm text-slate-500 mt-1">Scans every log, sub-entry, and note across all time.</p>
      </header>

      <div className="bg-white rounded-2xl border border-slate-200/70 p-4 md:p-5">
        <div className="relative">
          <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            ref={inputRef}
            data-testid="search-input"
            type="text"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Search notes, people, subjects, tasks…"
            className="w-full rounded-full border border-slate-200 pl-11 pr-11 py-3 focus:outline-none focus:ring-2 focus:ring-teal-400 text-base"
          />
          {term && (
            <button data-testid="search-clear" onClick={() => setTerm("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100">
              <X size={16} />
            </button>
          )}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-xs uppercase tracking-wider text-slate-400 mr-1">Filter by kind:</span>
          {Object.entries(kindLabels).map(([k, label]) => {
            const on = selectedKinds.has(k);
            return (
              <button key={k} data-testid={`search-filter-${k}`} onClick={() => toggleKind(k)}
                className={`text-xs font-medium px-3 py-1.5 rounded-full transition-all ${on ? kindClasses[k] + " ring-2 ring-offset-1 ring-slate-300" : "bg-slate-50 text-slate-500 hover:bg-slate-100"}`}>
                {label}
              </button>
            );
          })}
          {selectedKinds.size > 0 && (
            <button data-testid="search-filter-reset" onClick={() => setSelectedKinds(new Set())}
              className="text-xs text-slate-500 hover:text-slate-900 underline underline-offset-4 ml-1">
              Reset
            </button>
          )}
        </div>
      </div>

      <div className="min-h-[120px]">
        {!committedTerm && (
          <div data-testid="search-empty" className="text-center py-16 text-slate-400 text-sm">
            Type at least one character to search your journal.
          </div>
        )}
        {committedTerm && loading && (
          <div data-testid="search-loading" className="text-center py-16 text-slate-400 text-sm">Searching…</div>
        )}
        {committedTerm && !loading && error && (
          <div className="text-center py-16 text-rose-500 text-sm">{error}</div>
        )}
        {committedTerm && !loading && !error && (
          <>
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-wider text-slate-500" data-testid="search-count">
                {totalHits} match{totalHits === 1 ? "" : "es"} for “{committedTerm}”
              </div>
            </div>
            {totalHits === 0 ? (
              <div data-testid="search-no-results" className="text-center py-16 text-slate-400 text-sm border border-dashed border-slate-200 rounded-xl">
                Nothing matched. Try a shorter phrase or clear the filters.
              </div>
            ) : (
              <ul className="space-y-2" data-testid="search-results">
                {matches.map((m, i) => {
                  const s = m.sub;
                  const isGeneral = m.kind === "__notes__";
                  const label = isGeneral ? "General notes" : kindLabels[m.kind] || m.kind;
                  const cls = isGeneral ? "bg-slate-100 text-slate-600" : kindClasses[m.kind] || "bg-slate-100";
                  const title = isGeneral
                    ? (m.log.general_notes || "").slice(0, 120)
                    : (s.subject || s.process_type || s.position || kindLabels[s.kind] || "Entry");
                  const body = isGeneral
                    ? m.log.general_notes
                    : snippet(s);
                  const to = isGeneral
                    ? `/log?date=${m.log.log_date}`
                    : `/log?date=${m.log.log_date}&sub=${s.id}`;
                  const testid = isGeneral
                    ? `search-result-notes-${m.log.id}`
                    : `search-result-${s.id}`;
                  const people = s
                    ? [...(s.participants || []), ...(s.team || []), s.leader, s.applicant_name].filter(Boolean)
                    : [];
                  return (
                    <li key={`${m.log.id}-${s?.id || "notes"}-${i}`}>
                      <Link
                        to={to}
                        data-testid={testid}
                        className="group block bg-white border border-slate-200/70 rounded-xl p-4 hover:border-teal-300 hover:shadow-[0_10px_30px_-16px_rgba(2,6,23,0.18)] transition-all"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <span className={`text-[10px] px-2 py-1 rounded-full font-medium ${cls}`}>{label}</span>
                              <span className="text-xs text-slate-500">{m.log.log_date}</span>
                            </div>
                            <div className="text-sm font-medium text-slate-900">
                              <Highlight text={title || "Untitled entry"} term={committedTerm} />
                            </div>
                            {people.length > 0 && (
                              <div className="text-xs text-slate-500 mt-1">
                                <Highlight text={people.join(", ")} term={committedTerm} />
                              </div>
                            )}
                            {body && (
                              <div className="text-sm text-slate-600 mt-2 line-clamp-3 whitespace-pre-wrap">
                                <Highlight text={body} term={committedTerm} />
                              </div>
                            )}
                          </div>
                          <ArrowRight size={16} className="text-slate-300 group-hover:text-teal-500 mt-1 shrink-0" />
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  );
}
